import { useCallback, useEffect, useRef, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// QC-CORR-MODAL-001 — the fetch + acknowledge logic lifted verbatim from the
// former inline ProcessorFeedbackBanner. Same endpoints, same onAcknowledged patch.
// QC-CORRECTION-UI-001 — `qcStateKey` (order-level + component QC state from the
// parent order, refreshed via `tf:order-refresh` → order re-fetch) re-fetches
// feedback on change. No polling; an in-flight guard prevents duplicate calls.
export function useQcFeedback(orderId, onAcknowledged, qcStateKey) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const lastKey = useRef(qcStateKey);

  const fetchFeedback = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${orderId}/qc-feedback`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setData(await res.json());
      } else if (res.status === 403) {
        setData(null);
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Failed to load feedback');
      }
    } catch (e) {
      setError('Network error loading QC feedback');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { fetchFeedback(); }, [fetchFeedback]);

  useEffect(() => {
    if (lastKey.current === qcStateKey) return;
    lastKey.current = qcStateKey;
    fetchFeedback();
  }, [qcStateKey, fetchFeedback]);

  const acknowledge = useCallback(async () => {
    setAcking(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/orders/${orderId}/qc-acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const body = await res.json().catch(() => ({}));
        await fetchFeedback();
        if (onAcknowledged) onAcknowledged({
          qc_feedback_acknowledged_at: body?.qc_feedback_acknowledged_at || new Date().toISOString(),
        });
        return true;
      }
      const d = await res.json().catch(() => ({}));
      setError(d.detail || 'Failed to acknowledge');
    } catch (e) {
      setError('Network error');
    } finally {
      setAcking(false);
    }
    return false;
  }, [orderId, fetchFeedback, onAcknowledged]);

  return { data, loading, acking, error, acknowledge, refetch: fetchFeedback };
}
